import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { 
  Info, 
  Database, 
  Map, 
  Globe, 
  Smartphone, 
  Code, 
  ExternalLink,
  User,
  Award
} from 'lucide-react';

export function AboutPage() {
  const { language } = useLanguage();

  const t = {
    title: language === 'es' ? 'Acerca de' : 'About',
    subtitle: language === 'es' ? 'Información sobre el proyecto' : 'About this project',
    
    // Project description
    projectTitle: 'Tenerife Cerca',
    projectSubtitle: language === 'es' 
      ? 'Datos públicos útiles para tu día a día' 
      : 'Useful public data for your daily life',
    projectDescription: language === 'es'
      ? 'Aplicación web que reúne y visualiza datos abiertos del Cabildo de Tenerife y otras fuentes de datos abiertas públicas, para facilitar el acceso a información útil sobre servicios, transporte, naturaleza, cultura y accesibilidad de la isla.'
      : 'Web application that gathers and visualizes open data from the Cabildo de Tenerife and other public open data sources, to facilitate access to useful information about services, transport, nature, culture and accessibility on the island.',
    
    // Objectives
    objectivesTitle: language === 'es' ? 'Objetivos del Proyecto' : 'Project Objectives',
    objectives: language === 'es' ? [
      'Democratizar el acceso a los datos públicos de Tenerife',
      'Facilitar la búsqueda de servicios cercanos mediante geolocalización',
      'Promover el uso y reutilización de datos abiertos',
      'Ofrecer una herramienta útil para residentes y visitantes'
    ] : [
      'Democratize access to public data from Tenerife',
      'Facilitate the search for nearby services through geolocation',
      'Promote the use and reuse of open data',
      'Offer a useful tool for residents and visitors'
    ],
    
    // Data sources
    dataSourcesTitle: language === 'es' ? 'Fuentes de Datos' : 'Data Sources',
    dataSourcesDescription: language === 'es'
      ? 'Los datos mostrados en esta aplicación provienen de fuentes públicas oficiales:'
      : 'The data shown in this application comes from official public sources:',
    dataSources: [
      {
        name: language === 'es' ? 'Portal de Datos Abiertos del Cabildo de Tenerife' : 'Cabildo de Tenerife Open Data Portal',
        url: 'https://datos.tenerife.es',
        description: language === 'es' 
          ? 'Transporte, naturaleza, sanidad, hostelería, alojamientos, cultura, deportes, asociaciones y accesibilidad'
          : 'Transport, nature, health, hospitality, accommodations, culture, sports, associations and accessibility'
      },
      {
        name: 'Open-Meteo',
        url: 'https://open-meteo.com',
        description: language === 'es' ? 'Datos meteorológicos en tiempo real' : 'Real-time weather data'
      }
    ],
    
    // Features
    featuresTitle: language === 'es' ? 'Características Principales' : 'Main Features',
    features: language === 'es' ? [
      { icon: Map, text: 'Visualización en mapas interactivos' },
      { icon: Globe, text: 'Geolocalización para ver qué tienes cerca' },
      { icon: Smartphone, text: 'Diseño responsive (móvil y escritorio)' },
      { icon: Globe, text: 'Soporte bilingüe (Español/Inglés)' }
    ] : [
      { icon: Map, text: 'Interactive map visualization' },
      { icon: Globe, text: 'Geolocation to see what\'s nearby' },
      { icon: Smartphone, text: 'Responsive design (mobile and desktop)' },
      { icon: Globe, text: 'Bilingual support (Spanish/English)' }
    ],
    
    // Technologies
    technologiesTitle: language === 'es' ? 'Tecnologías Utilizadas' : 'Technologies Used',
    technologies: [
      'React',
      'FastAPI (Python)',
      'Leaflet (Mapas)',
      'Tailwind CSS',
      'APIs de Datos Abiertos'
    ],
    
    // Contest
    contestTitle: language === 'es' ? 'Concurso' : 'Contest',
    contestDescription: language === 'es'
      ? 'Aplicación desarrollada en el marco del Concurso de Datos Abiertos del Cabildo de Tenerife 2026'
      : 'Application developed within the framework of the Cabildo de Tenerife Open Data Contest 2026',
    
    // Author
    authorTitle: language === 'es' ? 'Autor' : 'Author',
    authorName: 'Roberto Rodríguez',
    authorWebsite: 'www.robertorguez.com',
    
    // Links
    visitPortal: language === 'es' ? 'Visitar portal de datos' : 'Visit data portal',
    visitWebsite: language === 'es' ? 'Visitar web' : 'Visit website',
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Info className="w-5 h-5 text-[#00599c]" />
          <span className="text-sm font-medium text-[#00599c] uppercase tracking-wider">
            {t.title}
          </span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#252a34] font-['Outfit'] mb-2">
          {t.projectTitle}
        </h1>
        <p className="text-lg text-[#00599c] font-medium">{t.projectSubtitle}</p>
      </div>

      {/* Project Description */}
      <Card className="mb-6 border-slate-200">
        <CardContent className="p-6">
          <p className="text-[#252a34]/80 leading-relaxed text-base">
            {language === 'es' ? (
              <>
                Aplicación web que reúne y visualiza{' '}
                <a 
                  href="https://datos.tenerife.es/es/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#00599c] hover:underline font-medium"
                >
                  datos abiertos del Cabildo de Tenerife
                </a>
                {' '}y otras fuentes de datos abiertas públicas, para facilitar el acceso a información útil sobre servicios, transporte, naturaleza, cultura y accesibilidad de la isla.
              </>
            ) : (
              <>
                Web application that gathers and visualizes{' '}
                <a 
                  href="https://datos.tenerife.es/es/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#00599c] hover:underline font-medium"
                >
                  open data from the Cabildo de Tenerife
                </a>
                {' '}and other public open data sources, to facilitate access to useful information about services, transport, nature, culture and accessibility on the island.
              </>
            )}
          </p>
          <div className="mt-4 p-3 bg-[#00599c]/5 rounded-lg border border-[#00599c]/10">
            <p className="text-sm text-[#252a34]/70">
              {language === 'es' 
                ? 'Los datos se actualizan automáticamente en cada consulta desde las fuentes abiertas del Cabildo de Tenerife. La fecha de actualización corresponde a la publicada por el propio Cabildo.'
                : 'Data is automatically updated on each query from the open sources of the Cabildo de Tenerife. The update date corresponds to that published by the Cabildo itself.'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Objectives */}
      <Card className="mb-6 border-slate-200">
        <CardContent className="p-6">
          <h2 className="text-xl font-bold text-[#252a34] font-['Outfit'] mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#00599c] flex items-center justify-center">
              <Database className="w-4 h-4 text-white" />
            </div>
            {t.objectivesTitle}
          </h2>
          <ul className="space-y-3">
            {t.objectives.map((objective, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#00599c] mt-2 flex-shrink-0" />
                <span className="text-[#252a34]/80">{objective}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Features */}
      <Card className="mb-6 border-slate-200">
        <CardContent className="p-6">
          <h2 className="text-xl font-bold text-[#252a34] font-['Outfit'] mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#22c55e] flex items-center justify-center">
              <Smartphone className="w-4 h-4 text-white" />
            </div>
            {t.featuresTitle}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {t.features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Icon className="w-5 h-5 text-[#22c55e]" />
                  <span className="text-[#252a34]/80 text-sm">{feature.text}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Data Sources */}
      <Card className="mb-6 border-slate-200">
        <CardContent className="p-6">
          <h2 className="text-xl font-bold text-[#252a34] font-['Outfit'] mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#f97316] flex items-center justify-center">
              <Database className="w-4 h-4 text-white" />
            </div>
            {t.dataSourcesTitle}
          </h2>
          <p className="text-[#252a34]/70 mb-4 text-sm">{t.dataSourcesDescription}</p>
          <div className="space-y-4">
            {t.dataSources.map((source, idx) => (
              <div key={idx} className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-[#252a34]">{source.name}</h3>
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-[#00599c] hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {t.visitPortal}
                  </a>
                </div>
                <p className="text-sm text-[#252a34]/60">{source.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Technologies */}
      <Card className="mb-6 border-slate-200">
        <CardContent className="p-6">
          <h2 className="text-xl font-bold text-[#252a34] font-['Outfit'] mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#8b5cf6] flex items-center justify-center">
              <Code className="w-4 h-4 text-white" />
            </div>
            {t.technologiesTitle}
          </h2>
          <div className="flex flex-wrap gap-2">
            {t.technologies.map((tech, idx) => (
              <span
                key={idx}
                className="px-3 py-1.5 bg-slate-100 text-[#252a34]/80 rounded-full text-sm font-medium"
              >
                {tech}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Contest & Author */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Contest */}
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-[#252a34] font-['Outfit'] mb-4 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#eab308] flex items-center justify-center">
                <Award className="w-4 h-4 text-white" />
              </div>
              {t.contestTitle}
            </h2>
            <p className="text-[#252a34]/80 text-sm">
              {t.contestDescription}
            </p>
          </CardContent>
        </Card>

        {/* Author */}
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-[#252a34] font-['Outfit'] mb-4 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#ec4899] flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              {t.authorTitle}
            </h2>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#00599c] flex items-center justify-center text-white font-bold text-lg">
                RR
              </div>
              <div>
                <p className="font-semibold text-[#252a34]">{t.authorName}</p>
                <a
                  href={`https://${t.authorWebsite}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-[#00599c] hover:underline"
                >
                  <Globe className="w-3 h-3" />
                  {t.authorWebsite}
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer note */}
      <div className="mt-8 text-center text-sm text-[#252a34]/50">
        <p>
          {language === 'es' 
            ? '© 2025 Tenerife Cerca. Datos proporcionados por fuentes públicas oficiales.'
            : '© 2025 Tenerife Cerca. Data provided by official public sources.'}
        </p>
      </div>
    </div>
  );
}
